import { useState, useCallback, useRef, useEffect } from 'react';
import CharLimitLabel from '@/components/base/CharLimitLabel';
import ImageUploader from '@/components/base/ImageUploader';
import api from '@/services/api';


export interface HeroSlide {
  headline: string;
  sub: string;
  cta: string;
  image: string;
  image_status?: string;
}

interface HeroEditorProps {
  slides: HeroSlide[];
  editing: boolean;
  onChange: (slides: HeroSlide[]) => void;
  onSaveAttempt?: () => boolean; // returns true if valid
}

function parseSlides(content: string[]): HeroSlide[] {
  if (!Array.isArray(content)) return [];
  return content.map((item) => {
    try {
      if (typeof item === 'object' && item !== null) {
        const i = item as any;
        return {
          headline: String(i.headline || ''),
          sub: String(i.sub || ''),
          cta: String(i.cta || ''),
          image: String(i.image || ''),
          image_status: i.image_status,
        };
      }
      const parsed = JSON.parse(item);
      return {
        headline: String(parsed.headline || ''),
        sub: String(parsed.sub || ''),
        cta: String(parsed.cta || ''),
        image: String(parsed.image || ''),
        image_status: parsed.image_status,
      };
    } catch {
      return { headline: String(item || ''), sub: '', cta: '', image: '' };
    }
  });
}

const LIMITS = {
  headline: { min: 10, max: 80 },
  sub: { min: 10, max: 120 },
  cta: { min: 2, max: 25 },
};

type FieldErrors = Record<string, Record<string, string>>;

function validateSlides(slides: HeroSlide[]): FieldErrors {
  const errors: FieldErrors = {};
  slides.forEach((slide, idx) => {
    const slideErrors: Record<string, string> = {};
    if (slide.headline.length < LIMITS.headline.min) {
      slideErrors.headline = `Headline must be at least ${LIMITS.headline.min} characters (currently ${slide.headline.length})`;
    }
    if (slide.sub.length < LIMITS.sub.min) {
      slideErrors.sub = `Subtext must be at least ${LIMITS.sub.min} characters (currently ${slide.sub.length})`;
    }
    if (slide.cta.length < LIMITS.cta.min) {
      slideErrors.cta = `Button text must be at least ${LIMITS.cta.min} characters (currently ${slide.cta.length})`;
    }
    if (Object.keys(slideErrors).length > 0) {
      errors[String(idx)] = slideErrors;
    }
  });
  return errors;
}

export function validateHeroSlides(slides: HeroSlide[]): boolean {
  return Object.keys(validateSlides(slides)).length === 0;
}

export default function HeroEditor({ slides: rawSlides, editing, onChange }: HeroEditorProps) {
  const slides = parseSlides(rawSlides);
  const [activeSlide, setActiveSlide] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [aiGenPrompt, setAiGenPrompt] = useState<Record<number, { prompt: string; loading: boolean }>>();
  const uploadErrorTimer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (uploadErrorTimer.current) clearTimeout(uploadErrorTimer.current);
    };
  }, []);

  const clearError = useCallback((slideIdx: number, field: string) => {
    setFieldErrors(prev => {
      const key = String(slideIdx);
      if (!prev[key]?.[field]) return prev;
      const next = { ...prev, [key]: { ...prev[key] } };
      delete next[key][field];
      if (Object.keys(next[key]).length === 0) delete next[key];
      return next;
    });
  }, []);

  const updateSlide = (index: number, field: keyof HeroSlide, value: string) => {
    const limit = LIMITS[field as keyof typeof LIMITS];
    if (limit && value.length > limit.max) return;
    const next = slides.map((s, i) => (i === index ? { ...s, [field]: value } : s));
    onChange(next);
    if (saveAttempted) clearError(index, field);
  };

  const addSlide = () => {
    if (slides.length >= 3) return;
    const next = [
      ...slides,
      { headline: '', sub: '', cta: '', image: '' },
    ];
    onChange(next);
    setActiveSlide(next.length - 1);
  };

  const removeSlide = (index: number) => {
    if (slides.length <= 1) return;
    const next = slides.filter((_, i) => i !== index);
    onChange(next);
    setActiveSlide(Math.max(0, activeSlide - 1));
    setFieldErrors(prev => {
      const next2: FieldErrors = {};
      Object.entries(prev).forEach(([k, v]) => {
        const n = Number(k);
        if (n < index) next2[String(n)] = v;
        else if (n > index) next2[String(n - 1)] = v;
      });
      return next2;
    });
  };

  const handleImageUpload = (index: number, _file: File, dataUrl: string) => {
    const next = slides.map((s, i) => (i === index ? { ...s, image: dataUrl, image_status: 'completed' } : s));
    onChange(next);
  };

  const handleAiGenerate = async (index: number) => {
    const prompt = aiGenPrompt?.[index]?.prompt?.trim();
    if (!prompt) return;
    setAiGenPrompt(prev => ({ ...prev, [index]: { prompt: prev?.[index]?.prompt || '', loading: true } }));
    
    try {
      const json = await api.generateWebsiteImage(prompt);
      if (json.status === 'success' && json.data?.url) {
        const next = slides.map((s, i) => (i === index ? { ...s, image: json.data.url, image_status: 'completed' } : s));
        onChange(next);
      }
    } catch (err) {
      console.error("Image generation failed:", err);
    } finally {
      setAiGenPrompt(prev => ({ ...prev, [index]: { prompt: '', loading: false } }));
    }
  };

  // Exposed via ref-like pattern: parent calls this to attempt save
  const attemptSave = (): boolean => {
    setSaveAttempted(true);
    const errors = validateSlides(slides);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Jump to first failing slide
      const firstFailIdx = Math.min(...Object.keys(errors).map(Number));
      setActiveSlide(firstFailIdx);
      return false;
    }
    return true;
  };

  // Attach attemptSave to the component instance via a data attribute trick
  // Parent ContentPreview will call window.__heroValidate() before toggling edit off
  if (editing) {
    (window as Record<string, unknown>).__heroValidate = attemptSave;
  }

  const getInputClass = (slideIdx: number, field: string, base: string) => {
    const hasError = !!fieldErrors[String(slideIdx)]?.[field];
    return `${base} ${hasError ? 'border-red-400 focus:border-red-400 bg-red-50/30' : 'border-gray-300 focus:border-gold'}`;
  };

  return (
    <div className="space-y-4">
      {/* Upload error toast */}
      {uploadError && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <i className="ri-error-warning-line text-red-500 text-xs"></i>
          <span className="text-xs text-red-600">{uploadError}</span>
        </div>
      )}

      {/* Empty states */}
      {slides.length === 0 && !editing && (
        <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center text-center">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#0859b8] mb-2.5">
            <i className="ri-layout-top-line text-lg"></i>
          </div>
          <h4 className="text-xs font-semibold text-gray-700 mb-1">No Hero Banner Configured</h4>
          <p className="text-[11px] text-gray-400 max-w-sm">Click "Generate Section" or "Generate Page" to generate AI copy and banner visuals, or click "Edit" to add slides manually.</p>
        </div>
      )}

      {slides.length === 0 && editing && (
        <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
          <p className="text-xs text-gray-500 mb-3">No slides in this hero banner yet.</p>
          <button
            onClick={addSlide}
            className="text-xs font-medium px-4 py-2 rounded-lg bg-[#0859b8] text-white hover:bg-[#0859b8]/90 transition-colors cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-add-line"></i>
            Add Slide
          </button>
        </div>
      )}

      {/* Slide tabs */}
      {slides.length > 0 && (
        <div className="flex items-center gap-2">
          {slides.map((_, idx) => {
            const hasError = !!fieldErrors[String(idx)];
            return (
              <button
                key={idx}
                onClick={() => setActiveSlide(idx)}
                className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${activeSlide === idx
                  ? 'bg-[#0859b8] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                Slide {idx + 1}
                {hasError && (
                  <i className="ri-error-warning-fill text-red-400 text-[10px]"></i>
                )}
              </button>
            );
          })}
          {editing && slides.length < 3 && (
            <button
              onClick={addSlide}
              className="text-xs font-medium px-3 py-1.5 rounded-lg border border-dashed border-gray-300 text-gray-500 hover:text-[#0b7f8f] hover:bg-[#e8f9fb] hover:border-[#e8f9fb] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-add-line"></i>
              Add Slide
            </button>
          )}
        </div>
      )}

      {/* Active slide editor / preview */}
      {slides.map((slide, idx) => (
        <div
          key={idx}
          className={`${activeSlide === idx ? 'block' : 'hidden'} bg-white border border-gray-200 rounded-xl overflow-hidden`}
        >
          {/* Image area */}
          <div className="relative">
            <div className="aspect-[21/9] bg-gray-100 relative overflow-hidden">
              {slide.image_status === 'pending' && !slide.image ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-[#0b7f8f] gap-3 bg-gray-50 animate-pulse">
                  <i className="ri-loader-4-line animate-spin text-3xl"></i>
                  <span className="text-xs font-medium">Generating image...</span>
                </div>
              ) : slide.image ? (
                <img src={slide.image} alt={`Banner ${idx + 1}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-2">
                  <i className="ri-image-line text-3xl"></i>
                  <span className="text-xs text-gray-400">
                    {slide.image_status === 'failed' 
                      ? 'Image generation could not be completed. You can upload an image below.' 
                      : 'Upload an image for this banner'}
                  </span>
                </div>
              )}
              {/* Hover upload button removed in favor of central Uploader below */}
            </div>
            {editing && (
              <div className="mt-3 mx-4">
                <ImageUploader 
                  type="hero" 
                  currentImage={slide.image} 
                  onUpload={(file, url) => handleImageUpload(idx, file, url)} 
                />
              </div>
            )}
            {editing && slides.length > 1 && (
              <button
                onClick={() => removeSlide(idx)}
                className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors cursor-pointer shadow-sm"
                title="Remove banner"
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            )}
          </div>

          {/* Text content */}
          <div className="p-4 space-y-3">
            {editing ? (
              <>
                {/* Headline */}
                <div>
                  <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide block mb-1">
                    Headline
                  </label>
                  <input
                    type="text"
                    value={slide.headline}
                    onChange={(e) => updateSlide(idx, 'headline', e.target.value)}
                    maxLength={LIMITS.headline.max}
                    className={`w-full text-sm font-medium text-gray-900 rounded-lg px-3 py-2 focus:outline-none transition-colors border ${getInputClass(idx, 'headline', '')}`}
                  />
                  <CharLimitLabel current={slide.headline.length} min={LIMITS.headline.min} max={LIMITS.headline.max} />
                  {fieldErrors[String(idx)]?.headline && (
                    <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {fieldErrors[String(idx)].headline}
                    </p>
                  )}
                </div>

                {/* Subtext */}
                <div>
                  <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide block mb-1">
                    Subtext
                  </label>
                  <input
                    type="text"
                    value={slide.sub}
                    onChange={(e) => updateSlide(idx, 'sub', e.target.value)}
                    maxLength={LIMITS.sub.max}
                    className={`w-full text-sm text-gray-600 rounded-lg px-3 py-2 focus:outline-none transition-colors border ${getInputClass(idx, 'sub', '')}`}
                  />
                  <CharLimitLabel current={slide.sub.length} min={LIMITS.sub.min} max={LIMITS.sub.max} />
                  {fieldErrors[String(idx)]?.sub && (
                    <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {fieldErrors[String(idx)].sub}
                    </p>
                  )}
                </div>

                {/* CTA */}
                <div>
                  <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide block mb-1">
                    CTA Button Text
                  </label>
                  <input
                    type="text"
                    value={slide.cta}
                    onChange={(e) => updateSlide(idx, 'cta', e.target.value)}
                    maxLength={LIMITS.cta.max}
                    className={`w-full text-sm text-gray-600 rounded-lg px-3 py-2 focus:outline-none transition-colors border ${getInputClass(idx, 'cta', '')}`}
                  />
                  <CharLimitLabel current={slide.cta.length} min={LIMITS.cta.min} max={LIMITS.cta.max} />
                  {fieldErrors[String(idx)]?.cta && (
                    <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {fieldErrors[String(idx)].cta}
                    </p>
                  )}
                </div>

                {/* AI Image Generation */}
                {!slide.image && (
                  <div className="space-y-2 px-3 py-2.5 bg-navy/5 rounded-lg">
                    <div className="flex items-start gap-2">
                      <i className="ri-magic-line text-navy text-xs mt-0.5"></i>
                      <p className="text-xs text-navy leading-relaxed">
                        No image? Describe what you want and our AI will generate a high-quality banner for you.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="e.g., Modern city skyline at golden hour..."
                        value={aiGenPrompt?.[idx]?.prompt || ''}
                        onChange={(e) => setAiGenPrompt(prev => ({ ...prev, [idx]: { prompt: e.target.value, loading: prev?.[idx]?.loading || false } }))}
                        className="flex-1 text-xs text-gray-700 rounded-md px-3 py-2 border border-gray-300 focus:border-gold focus:outline-none"
                        maxLength={200}
                      />
                      <button
                        onClick={() => handleAiGenerate(idx)}
                        disabled={!aiGenPrompt?.[idx]?.prompt?.trim() || aiGenPrompt?.[idx]?.loading}
                        className="text-xs font-medium px-3 py-2 rounded-md bg-gold text-white hover:bg-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                      >
                        {aiGenPrompt?.[idx]?.loading ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Generating...
                          </>
                        ) : (
                          <>
                            <i className="ri-magic-line"></i>
                            Generate
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-gray-900">{slide.headline}</h4>
                <p className="text-xs text-gray-500">{slide.sub}</p>
                <span className="inline-block mt-1 text-xs font-medium bg-[#e8f9fb] text-[#0b7f8f] border border-[#cdeff3] px-2.5 py-1 rounded">
                  {slide.cta}
                </span>
                {!slide.image && (
                  <p className="text-[10px] text-gray-400 mt-2 italic">
                    AI-generated image will be created on publish
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      ))}

      <p className="text-[10px] text-gray-400">
        {slides.length}/3 banners · Minimum 1, maximum 3
      </p>
    </div>
  );
}